/**
 * Shared parts of the Opus landing design for /pricing, /faq and the compare pages (T855), ported from
 * docs/design/html/flows/web-landing-*.mjs. They reuse the T854 icon tile and the home route backdrop; no new tokens.
 */
import { Check, X, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { RouteBackdrop } from "../home/home-art"
import { IconTile } from "./product-art"

type Tone = Parameters<typeof IconTile>[0]["tone"]

/** Muted header band with the route backdrop behind it (desktop only; decorative). */
export const HeroBand = ({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) => (
  <header
    // Test hook (T856): the band has no role to query by.
    data-hero-band=""
    className={`bg-muted/40 px-band relative isolate grid gap-4 overflow-hidden py-8 md:py-12 ${className}`}
  >
    <RouteBackdrop />
    {children}
  </header>
)

/** Key facts as a row of cards. Values are copy, never invented numbers. */
export const StatRow = ({
  label,
  stats,
}: {
  label: string
  stats: readonly { value: string; label: string; icon?: LucideIcon; tone?: Tone }[]
}) => (
  <ul aria-label={label} className="flex flex-wrap gap-3">
    {stats.map(({ value, label: text, icon, tone }) => (
      <li
        key={value}
        className="border-border bg-card flex items-center gap-3 rounded-lg border px-4 py-2"
      >
        {icon && <IconTile icon={icon} tone={tone ?? "primary"} />}
        <span>
          <b className="block text-lg">{value}</b>
          <span className="text-muted-foreground text-sm">{text}</span>
        </span>
      </li>
    ))}
  </ul>
)

/** "Pick this if" cards. Both sides get the same markup and weight. */
export const PickPair = ({
  items,
}: {
  items: { icon: LucideIcon; tone?: Tone; title: string; body: ReactNode }[]
}) => (
  <div className="grid gap-4 md:grid-cols-2">
    {items.map(({ icon, tone, title, body }) => (
      <div
        key={title}
        className="border-border bg-card grid content-start gap-2 rounded-xl border p-5"
      >
        <h3 className="flex items-center gap-3 text-lg font-semibold">
          <IconTile icon={icon} tone={tone} />
          <span>{title}</span>
        </h3>
        <p className="text-muted-foreground">{body}</p>
      </div>
    ))}
  </div>
)

/** Section h2 with a decorative icon tile in front; the heading text carries the meaning. */
export const SectionTitle = ({
  id,
  icon,
  tone,
  children,
}: {
  id?: string
  icon: LucideIcon
  tone?: Tone
  children: ReactNode
}) => (
  <h2 id={id} className="flex items-center gap-3 text-2xl font-semibold">
    <IconTile icon={icon} tone={tone} />
    <span>{children}</span>
  </h2>
)

/** "You can" / "You cannot" card: state is in the heading words, the mark beside each item is decorative. */
export const GlanceCard = ({
  icon,
  tone,
  title,
  items,
  mark,
}: {
  icon: LucideIcon
  tone?: Tone
  title: string
  items: ReactNode[]
  mark: "check" | "cross"
}) => {
  const Mark = mark === "check" ? Check : X
  return (
    <div className="border-border bg-card grid content-start gap-3 rounded-xl border p-5">
      <h3 className="flex items-center gap-3 text-lg font-semibold">
        <IconTile icon={icon} tone={tone} />
        <span>{title}</span>
      </h3>
      <ul className="grid gap-2">
        {items.map((item, k) => (
          <li key={k} className="flex items-start gap-2.5">
            <Mark aria-hidden="true" className="text-muted-foreground mt-1 size-4 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The road between two steps: a column on phones, a row from md. Asphalt (bg-muted, as in RouteBackdrop) with a
 * dashed warning-colour centre line drawn as a repeating gradient. Full class names so Tailwind can see them.
 */
const Road = () => (
  <span
    aria-hidden="true"
    className="bg-muted absolute top-12 -bottom-5 left-[15px] w-2.5 rounded-full bg-[image:repeating-linear-gradient(180deg,var(--color-warning)_0_12px,transparent_0_22px)] bg-[length:2px_100%] bg-[position:50%_0] bg-no-repeat md:top-[15px] md:right-[-0.75rem] md:bottom-auto md:left-14 md:h-2.5 md:w-auto md:bg-[image:repeating-linear-gradient(90deg,var(--color-warning)_0_12px,transparent_0_22px)] md:bg-[length:100%_2px] md:bg-[position:0_50%]"
  />
)

/**
 * Numbered or icon steps joined by a road line (web-landing-* routeSteps): a row from md, a column on phones.
 * The marker is decorative (the heading carries the step); the road is a CSS gradient with the yellow dashes of
 * RouteBackdrop. No new tokens.
 */
export const RouteSteps = ({
  steps,
  label,
}: {
  steps: readonly { title: string; body: ReactNode; icon?: LucideIcon }[]
  label?: string
}) => (
  <ol
    data-route-steps=""
    aria-label={label}
    className="grid gap-5 md:auto-cols-fr md:grid-flow-col md:gap-6"
  >
    {steps.map(({ title, body, icon: Icon }, k) => (
      <li
        key={title}
        className="relative grid grid-cols-[2.5rem_1fr] content-start gap-x-3.5 md:grid-cols-1 md:gap-y-3"
      >
        <span
          aria-hidden="true"
          className="bg-primary text-primary-foreground ring-background relative z-10 grid size-10 place-items-center rounded-full font-bold ring-[6px]"
        >
          {Icon ? <Icon className="size-5" /> : k + 1}
        </span>
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-muted-foreground mt-1">{body}</p>
        </div>
        {k < steps.length - 1 && <Road />}
      </li>
    ))}
  </ol>
)
